import React, { useEffect, useMemo, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, Plus, Trash2, ExternalLink, FileText, Youtube, Link2, Paperclip,
  Search, FolderOpen, X, Globe, Lock, Download, Image as ImageIcon, Film, File as FileIcon,
  GraduationCap,
} from "lucide-react";

const groupLabel = (v) => (v && v.includes("|") ? v.split("|").join(" · ") : v || "");

const iconFor = (r) => {
  if (r.kind === "link") {
    if ((r.url || "").match(/youtu\.?be/)) return Youtube;
    return Link2;
  }
  const ct = r.content_type || "";
  if (ct.startsWith("image/")) return ImageIcon;
  if (ct.startsWith("video/")) return Film;
  if (ct.includes("pdf")) return FileText;
  return FileIcon;
};

const ytId = (url) => {
  const m = (url || "").match(/(?:youtu\.be\/|v=|embed\/)([\w-]{11})/);
  return m ? m[1] : null;
};

const VIS_LABEL = { publico: "Público", colegio: "Colegio", privado: "Privado" };

export default function BancoRecursos({ profile }) {
  const grades = useMemo(() => (profile?.all_grades || []).map((g) => g.grade), [profile]);
  const [items, setItems] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [mySubject, setMySubject] = useState("");
  const [loading, setLoading] = useState(true);
  const [fGrade, setFGrade] = useState("");
  const [fSubject, setFSubject] = useState("");
  const [q, setQ] = useState("");
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/docente/resources", { params: { grade: fGrade || undefined, subject: fSubject || undefined, q: q || undefined } });
      setItems(data.resources || []);
      setSubjects(data.subjects || []);
      setMySubject(data.my_subject || "");
    } catch { toast.error("No se pudo cargar el banco de recursos"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fGrade, fSubject]);

  const del = async (id) => {
    if (!window.confirm("¿Eliminar este recurso?")) return;
    try { await api.delete(`/docente/resources/${id}`); setItems((x) => x.filter((r) => r.id !== id)); toast.success("Recurso eliminado"); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo eliminar"); }
  };

  const openFile = (r) => {
    if (r.kind === "link") { window.open(r.url, "_blank", "noopener"); return; }
    window.open(`${API}${r.file_url}`, "_blank", "noopener");
  };

  return (
    <div className="space-y-5" data-testid="docente-recursos">
      <div className="doc-glass rounded-2xl p-5 border border-white/50 shadow-lg">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[10rem]">
            <label className="text-[10px] uppercase tracking-[0.2em] text-navy-light block mb-1">Buscar</label>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-nardo-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} data-testid="res-search"
                placeholder="Título o descripción…" className="doc-input pl-9 w-full" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-navy-light block mb-1">Grupo</label>
            <select value={fGrade} onChange={(e) => setFGrade(e.target.value)} data-testid="res-filter-grade" className="doc-input min-w-[8rem]">
              <option value="">Todos</option>
              {grades.map((g) => <option key={g} value={g}>{groupLabel(g)}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-navy-light block mb-1">Materia</label>
            <select value={fSubject} onChange={(e) => setFSubject(e.target.value)} data-testid="res-filter-subject" className="doc-input min-w-[8rem]">
              <option value="">Todas</option>
              {subjects.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <button onClick={() => setShowForm(true)} data-testid="res-add-btn"
            className="text-xs inline-flex items-center gap-1.5 bg-navy hover:bg-navy-light text-white px-4 py-2 rounded-lg font-semibold">
            <Plus className="w-4 h-4" /> Subir recurso
          </button>
        </div>
        <p className="text-[11px] text-nardo-500 mt-3">Biblioteca del colegio: comparte material por grado y materia. Los recursos "del colegio" los ven todos los docentes; los "privados" solo tú.</p>
      </div>

      {loading ? (
        <div className="py-16 text-center text-nardo-500 text-sm"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Cargando…</div>
      ) : items.length === 0 ? (
        <div className="doc-glass rounded-2xl border border-white/50 py-16 text-center text-nardo-500 text-sm">
          <FolderOpen className="w-10 h-10 mx-auto mb-3 text-nardo-300" />
          Aún no hay recursos. Sube el primero con "Subir recurso".
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="res-list">
          {items.map((r) => {
            const Icon = iconFor(r);
            const ct = r.content_type || "";
            const isImg = r.kind === "file" && ct.startsWith("image/");
            const isPdf = r.kind === "file" && ct.includes("pdf");
            const yt = r.kind === "link" ? ytId(r.url) : null;
            const fileHref = r.file_url ? `${API}${r.file_url}` : null;
            return (
              <div key={r.id} data-testid={`res-card-${r.id}`} className="doc-glass rounded-2xl border border-white/50 shadow p-4 flex flex-col">
                {(isImg || isPdf || yt) && (
                  <div className="mb-3 -mx-1 rounded-xl overflow-hidden bg-nardo-100 h-32 flex items-center justify-center cursor-pointer" onClick={() => openFile(r)} data-testid={`res-preview-${r.id}`}>
                    {isImg && <img src={fileHref} alt={r.title} className="w-full h-full object-cover" loading="lazy" />}
                    {yt && <img src={`https://img.youtube.com/vi/${yt}/mqdefault.jpg`} alt={r.title} className="w-full h-full object-cover" loading="lazy" />}
                    {isPdf && <iframe src={`${fileHref}#toolbar=0&navpanes=0&view=FitH`} title={r.title} className="w-full h-full pointer-events-none" />}
                  </div>
                )}
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-navy/10 text-navy flex items-center justify-center shrink-0"><Icon className="w-5 h-5" /></div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-navy text-sm leading-tight truncate">{r.title}</div>
                    <div className="text-[11px] text-nardo-500 mt-0.5 flex items-center gap-1">
                      {r.visibility === "privado" ? <Lock className="w-3 h-3" /> : r.visibility === "publico" ? <GraduationCap className="w-3 h-3" /> : <Globe className="w-3 h-3" />}
                      {VIS_LABEL[r.visibility] || "Colegio"} · {r.docente_name}
                    </div>
                  </div>
                </div>
                {r.description && <p className="text-xs text-nardo-600 mt-3 line-clamp-3">{r.description}</p>}
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {r.subject && <span className="text-[10px] px-2 py-0.5 rounded-full bg-navy/10 text-navy font-medium">{r.subject}</span>}
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-700 font-medium">{r.grade_label}</span>
                </div>
                <div className="flex items-center gap-2 mt-4 pt-3 border-t border-white/50">
                  <button onClick={() => openFile(r)} data-testid={`res-open-${r.id}`}
                    className="flex-1 text-xs inline-flex items-center justify-center gap-1.5 border border-navy text-navy hover:bg-navy hover:text-white px-3 py-1.5 rounded-lg font-semibold transition-colors">
                    {r.kind === "link" ? <ExternalLink className="w-3.5 h-3.5" /> : <Download className="w-3.5 h-3.5" />}
                    {r.kind === "link" ? "Abrir enlace" : "Abrir archivo"}
                  </button>
                  {r.can_delete && (
                    <button onClick={() => del(r.id)} data-testid={`res-del-${r.id}`} className="text-red-500 hover:bg-red-50 p-1.5 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showForm && (
        <ResourceForm profile={profile} mySubject={mySubject} grades={grades} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />
      )}
    </div>
  );
}

function ResourceForm({ mySubject, grades, onClose, onSaved }) {
  const [tab, setTab] = useState("file");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [subject, setSubject] = useState(mySubject || "");
  const [grade, setGrade] = useState("");
  const [visibility, setVisibility] = useState("colegio");
  const [url, setUrl] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!title.trim()) { toast.error("El título es obligatorio"); return; }
    if (tab === "file" && !file) { toast.error("Selecciona un archivo"); return; }
    if (tab === "link" && !url.trim()) { toast.error("Escribe un enlace"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("title", title.trim());
      fd.append("description", description.trim());
      fd.append("subject", subject.trim());
      fd.append("grade", grade);
      fd.append("visibility", visibility);
      if (tab === "file") fd.append("file", file);
      else fd.append("url", url.trim());
      await api.post("/docente/resources", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Recurso agregado");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-navy-ink/70 flex items-start justify-center overflow-y-auto p-4" onClick={onClose} data-testid="res-form-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-nardo-200">
          <div className="font-display font-bold text-navy">Nuevo recurso</div>
          <button onClick={onClose} className="text-nardo-500 hover:text-navy p-1"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex gap-2">
            <button onClick={() => setTab("file")} data-testid="res-tab-file" className={`flex-1 text-xs font-semibold py-2 rounded-lg inline-flex items-center justify-center gap-1.5 ${tab === "file" ? "bg-navy text-white" : "bg-nardo-100 text-nardo-600"}`}><Paperclip className="w-4 h-4" /> Archivo</button>
            <button onClick={() => setTab("link")} data-testid="res-tab-link" className={`flex-1 text-xs font-semibold py-2 rounded-lg inline-flex items-center justify-center gap-1.5 ${tab === "link" ? "bg-navy text-white" : "bg-nardo-100 text-nardo-600"}`}><Link2 className="w-4 h-4" /> Enlace</button>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-navy-light block mb-1">Título *</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="res-title" className="doc-input w-full" placeholder="Ej. Guía de fracciones" />
          </div>
          {tab === "file" ? (
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-navy-light block mb-1">Archivo (PDF, imagen, Word, PPT…) — máx 25 MB</label>
              <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="res-file" className="doc-input w-full text-xs" />
            </div>
          ) : (
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-navy-light block mb-1">Enlace (YouTube, Drive, web…)</label>
              <input value={url} onChange={(e) => setUrl(e.target.value)} data-testid="res-url" className="doc-input w-full" placeholder="https://…" />
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-navy-light block mb-1">Descripción</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} data-testid="res-desc" rows={2} className="doc-input w-full resize-none" placeholder="Opcional" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-navy-light block mb-1">Materia</label>
              <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="res-subject" className="doc-input w-full" placeholder="Materia" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-navy-light block mb-1">Grupo</label>
              <select value={grade} onChange={(e) => setGrade(e.target.value)} data-testid="res-grade" className="doc-input w-full">
                <option value="">Todos los grados</option>
                {grades.map((g) => <option key={g} value={g}>{groupLabel(g)}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-navy-light block mb-1">Visibilidad</label>
            <div className="grid grid-cols-3 gap-2">
              <button onClick={() => setVisibility("colegio")} data-testid="res-vis-colegio" className={`text-[11px] font-semibold py-2 rounded-lg inline-flex flex-col items-center justify-center gap-0.5 ${visibility === "colegio" ? "bg-navy text-white" : "bg-nardo-100 text-nardo-600"}`}><Globe className="w-4 h-4" /> Colegio</button>
              <button onClick={() => setVisibility("publico")} data-testid="res-vis-publico" className={`text-[11px] font-semibold py-2 rounded-lg inline-flex flex-col items-center justify-center gap-0.5 ${visibility === "publico" ? "bg-navy text-white" : "bg-nardo-100 text-nardo-600"}`}><GraduationCap className="w-4 h-4" /> Alumnos</button>
              <button onClick={() => setVisibility("privado")} data-testid="res-vis-privado" className={`text-[11px] font-semibold py-2 rounded-lg inline-flex flex-col items-center justify-center gap-0.5 ${visibility === "privado" ? "bg-navy text-white" : "bg-nardo-100 text-nardo-600"}`}><Lock className="w-4 h-4" /> Solo yo</button>
            </div>
            <p className="text-[10px] text-nardo-500 mt-1.5">
              {visibility === "publico" ? "Visible para docentes y también para los alumnos/padres del grado seleccionado." : visibility === "privado" ? "Solo tú lo verás." : "Visible para todos los docentes del colegio."}
            </p>
          </div>
          <button onClick={submit} disabled={saving} data-testid="res-submit"
            className="w-full text-sm inline-flex items-center justify-center gap-2 bg-navy hover:bg-navy-light text-white py-2.5 rounded-lg font-semibold disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Guardar recurso
          </button>
        </div>
      </div>
    </div>
  );
}
